import type { Tables } from "../database.types";

export type DocumentStructureItemType = "folder" | "file";

export interface DocumentStructure<T extends { file: Tables<"files"> }> {
	items: Array<DocumentStructureItem<T>>;
}

export type DocumentStructureItem<T extends { file: Tables<"files"> }> =
	| DocumentStructureFileItem<T>
	| DocumentStructureFolderItem<T>;

export type DocumentStructureFileItem<T extends { file: Tables<"files"> }> = {
	id: string;
	type: "file";
	item: T;
	file: Tables<"files">;
};

export type DocumentStructureFolderItem<T extends { file: Tables<"files"> }> = {
	id: string;
	type: "folder";
	name: string;
	items: Array<DocumentStructureFileItem<T> | DocumentStructureFolderItem<T>>;
	fullPath: string; // Optional, for convenience
};

/**
 * The document collections the API serves, and the whole of them.
 *
 * `/documents/:documentFilesTable/:identifier` takes its table from the URL, so this list is the
 * entire difference between the five collections the clients open and every table in the database
 * whose name ends in `Files` — thirteen of them, including `invoiceFiles` and `partnerReportFiles`,
 * which carry commercial records that no rule about attachments describes.
 *
 * The server's registry (`apps/server/src/handlers/documents/registry.ts`) declares one entry per
 * value and does not compile without it, so a collection is added here and there together or not
 * at all. A value outside this list is a validation error, not a query.
 */
export const DOCUMENT_FILES_TABLES = [
	"organizationWorksiteDocumentFiles",
	"projectDocumentFiles",
	"protocolFiles",
	"serviceCalculationFiles",
	"vehicleDocumentFiles",
] as const;

export type DocumentFilesTable = (typeof DOCUMENT_FILES_TABLES)[number];

/**
 * The collections that classify their documents, which is to say the ones whose junction table
 * carries a type column — the risk-assessment marker the project and worksite explorers set.
 *
 * The reclassify route accepts only these, because a type patch against a collection with no such
 * column is a mistake in the request rather than an update: sent anywhere else it would match
 * nothing and answer as though it had worked. `documents.test.ts` compares this list against the
 * registry's own type columns, so the two cannot drift.
 */
export const CLASSIFYING_DOCUMENT_FILES_TABLES = [
	"organizationWorksiteDocumentFiles",
	"projectDocumentFiles",
] as const satisfies ReadonlyArray<DocumentFilesTable>;

export type ClassifyingDocumentFilesTable = (typeof CLASSIFYING_DOCUMENT_FILES_TABLES)[number];
